import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  Box,
  ButtonBase,
  Button,
  Collapse,
  FormControl,
  Typography,
} from '@mui/material';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import { memo, useCallback, useMemo, useState } from 'react';
import { bloxValues } from 'themes/constants';
import {
  getMockOutlinedInputSx,
  getMockOutlinedLabelSx,
  resolveSxProps,
} from 'utility/styles';
import {
  DynamicFieldType,
  DynamicFormFieldWithContext,
  TDynamicFieldOnChange,
} from '../DynamicFormField';
import { useTranslation } from 'react-i18next';
import { getFieldLabel } from '../utility/functions';

/**
 * ---------------------------------------
 * Group Dynamic Field Renderer
 * See Dynamic Fields Spec: Group Fields
 * ---------------------------------------
 *
 * Renders a group field as a fieldset w/ repeatable fields ("rows").
 * Each row has an entry header with remove action.
 * An add-row action is shown at the bottom of the group.
 *
 */

type GroupDynamicFieldProps = {
  fieldConfig: DynamicFieldType;
  selectedValue: any;
  disabled: boolean;
  handleValueChange: TDynamicFieldOnChange;
  fieldSx?: SxProps<Theme>;
};

export const GroupDynamicField = memo(
  ({
    fieldConfig,
    selectedValue,
    disabled,
    handleValueChange,
    fieldSx,
  }: GroupDynamicFieldProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const strRemove = t('blox.actions.remove');
    const strAddEntry = t('blox.actions.add_entry');
    const strEntry = t('blox.general.entry');
    const strNoEntries = t('blox.general.no_entries');
    const strExpand = t('blox.actions.expand');
    const strNoFieldsConfigured = t('blox.general.no_fields_configured');

    const fieldLabel = useMemo(() => {
      return getFieldLabel(fieldConfig);
    }, [fieldConfig]);

    const fieldName = useMemo(() => {
      return fieldConfig?.name ?? '';
    }, [fieldConfig?.name]);

    const testId = useMemo(() => {
      return `dynamic-field-group-${fieldName}`;
    }, [fieldName]);

    const fieldChildren = useMemo(() => {
      return fieldConfig?.config?.children ?? [];
    }, [fieldConfig?.config?.children]);

    const rows = useMemo(
      () => (Array.isArray(selectedValue) ? selectedValue : []),
      [selectedValue],
    );

    const [collapsed, setCollapsed] = useState(fieldConfig?.config?.collapsed ?? false);

    /**
     * Component Event Handlers
     */
    const handleToggleCollapse = useCallback(() => {
      setCollapsed((prev) => !prev);
    }, []);

    // Builds handler for removing a row at a given index (repeatable group)
    const handleRemoveRow = useCallback(
      (rowIndex: number) => {
        if (rowIndex < 0 || rowIndex >= rows.length) return;
        const updatedRows = rows.filter((_value: any, i: number) => i !== rowIndex);
        handleValueChange(fieldConfig?.name, updatedRows);
      },
      [rows, handleValueChange, fieldConfig?.name],
    );

    const buildNewRow = useCallback((): Record<string, any> => {
      const newRow: Record<string, any> = {};
      fieldChildren.forEach((child) => {
        if (child?.type === 'group') {
          newRow[child?.name] = Array.isArray(child?.default) ? child?.default : [];
          return;
        }
        newRow[child.name] = child.default ?? null;
      });

      return newRow;
    }, [fieldChildren]);

    // Adds a new default row
    const handleAddRow = useCallback(() => {
      if (!fieldChildren.length) return;
      handleValueChange(fieldConfig?.name, [...rows, buildNewRow()]);
    }, [fieldChildren.length, rows, buildNewRow, handleValueChange, fieldConfig?.name]);

    // Builds handler for child onChange within a repeatable group row
    const handleArrayChildChange = useCallback(
      (rowIndex: number, childName: string, childValue: any) => {
        const updatedRows = [...rows];
        updatedRows[rowIndex] = {
          ...(updatedRows[rowIndex] ?? {}),
          [childName]: childValue,
        };
        handleValueChange(fieldConfig?.name, updatedRows);
      },
      [rows, handleValueChange, fieldConfig?.name],
    );

    /**
     * Styles
     */

    // Mui border/outlined outlined style
    const mockOutlinedStyles = useMemo(
      () => getMockOutlinedInputSx(theme, false),
      [theme],
    );

    // Solid gray for label hover to cover up border
    const mockLabelSx = useMemo(() => getMockOutlinedLabelSx(theme, true), [theme]);

    // Resolve the top level fieldSx to object
    const resolvedSx: SxProps<Theme> = useMemo(() => {
      return resolveSxProps(fieldSx, theme);
    }, [fieldSx, theme]);

    // Base fieldset styles
    // Needs some mt for mock outlined label offset.
    const fieldsetSx: SxProps<Theme> = useMemo(() => {
      return {
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        borderRadius: 1,
        gap: 0.5,
        mt: 1.5,
        pt: 3,
        pb: 1,
        px: 1,
        ...mockOutlinedStyles,
        ...resolvedSx,
      };
    }, [mockOutlinedStyles, resolvedSx]);

    // Rotate label arrow when collapsed
    const arrowIconSx: SxProps<Theme> = useMemo(() => {
      return {
        fontSize: 20,
        ml: 0.25,
        transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
        transition: theme.transitions.create('transform', {
          duration: theme.transitions.duration.short,
        }),
      };
    }, [collapsed, theme]);

    /**
     * Render Logic
     * 1. If no children, render disabled placeholder with message
     * 2. If children, render fieldset with collapsible
     */

    /**
     * No children - empty / invalid placeholder
     */
    if (!fieldChildren?.length) {
      return (
        <FormControl
          data-testid={testId}
          variant="outlined"
          fullWidth
          sx={fieldsetSx}
        >
          <ButtonBase
            data-testid={`${testId}-label-button`}
            sx={mockLabelSx}
            disabled
          >
            {fieldLabel}
          </ButtonBase>
          <Typography
            data-testid={`${testId}-no-fields`}
            sx={{
              fontSize: bloxValues.smallFontSizeRem,
              color: theme.palette.text.disabled,
              py: 1,
              textAlign: 'center',
            }}
          >
            {strNoFieldsConfigured}
          </Typography>
        </FormControl>
      );
    }

    /**
     * Groups render as one fieldset of repeatable "rows".
     */
    return (
      <FormControl
        data-testid={testId}
        variant="outlined"
        fullWidth
        sx={fieldsetSx}
      >
        <ButtonBase
          disabled={disabled}
          data-testid={`${testId}-label-button`}
          aria-label={`Toggle ${fieldLabel}`}
          sx={mockLabelSx}
          onClick={handleToggleCollapse}
        >
          <Typography sx={{ fontSize: bloxValues.smallFontSizeRem, color: 'inherit' }}>
            {fieldLabel}
          </Typography>
          <ArrowDropDownIcon sx={arrowIconSx} />
        </ButtonBase>

        {collapsed && (
          <Button
            data-testid={`${testId}-expand-button`}
            variant="text"
            disabled={disabled}
            onClick={handleToggleCollapse}
            fullWidth={true}
            sx={{
              justifyContent: 'center',
              p: 1,
              color: theme.palette.text.disabled,
              fontSize: bloxValues.smallFontSizeRem,
              fontWeight: bloxValues.fontWeightMedium,
            }}
          >
            {strExpand}...
          </Button>
        )}

        <Collapse in={!collapsed}>
          <Box
            data-testid={`${testId}-entries-container`}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {rows.length === 0 && (
              <Typography
                sx={{
                  fontSize: bloxValues.smallFontSizeRem,
                  color: theme.palette.text.disabled,
                  py: 0.5,
                }}
              >
                {strNoEntries}
              </Typography>
            )}

            {rows.map((row: Record<string, any>, rowIndex: number) => {
              const rowTestId = `${testId}-row-${rowIndex}`;
              const rowPosition = rowIndex + 1;
              return (
                <Box
                  key={rowTestId}
                  data-testid={rowTestId}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    p: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      px: 0.5,
                      py: 0.25,
                      borderRadius: 1,
                      bgcolor: theme.palette.action.hover,
                    }}
                  >
                    <Typography
                      sx={{
                        px: 0.5,
                        color: theme.palette.text.secondary,
                        fontSize: bloxValues.smallFontSizeRem,
                      }}
                    >
                      {strEntry} {rowPosition}
                    </Typography>

                    <Button
                      data-testid={`${testId}-remove-row-${rowIndex}`}
                      disabled={disabled}
                      onClick={() => handleRemoveRow(rowIndex)}
                      aria-label={`Remove ${fieldLabel} entry ${rowPosition}`}
                      size="small"
                      variant="text"
                      color="error"
                      sx={{
                        fontSize: bloxValues.smallFontSizeRem,
                        fontWeight: bloxValues.fontWeightMedium,
                      }}
                    >
                      {strRemove}
                    </Button>
                  </Box>

                  {fieldChildren.map((child) => (
                    <DynamicFormFieldWithContext
                      key={`${child.name}-${rowIndex}`}
                      fieldConfig={child}
                      value={row?.[child.name]}
                      onChange={(_childName, childValue) => {
                        handleArrayChildChange(rowIndex, child.name, childValue);
                      }}
                      disabled={disabled}
                    />
                  ))}
                </Box>
              );
            })}

            <Button
              data-testid={`${testId}-add-row`}
              size="small"
              fullWidth
              disabled={disabled}
              onClick={handleAddRow}
              sx={{ justifyContent: 'center' }}
            >
              {strAddEntry}
            </Button>
          </Box>
        </Collapse>
      </FormControl>
    );
  },
);
GroupDynamicField.displayName = 'GroupDynamicField';
